/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import classNames from 'classnames';
import { get, isEmpty, throttle, toLower, values } from 'lodash';
import PropTypes from 'prop-types';

import { Button, CoreCost, TertiaryButton } from 'app/common/components';
import { AssetType, PriceType } from 'app/common/constants';
import {
  useFormatMessage,
  useProductClick,
  useProductUrl,
  useWindowEventListener,
  useNationalSiteContext
} from 'app/common/hooks';

import { MiniProductGridContext } from '../../contexts';
import {
  MiniProductAddToCart,
  MiniProductAsset,
  MiniProductPrices
} from './components';
import ItemAvailability from 'app/common/components/ItemAvailability';
import InventoryIndicatorModal from 'app/common/components/ItemAvailability/components/InventoryIndicatorModal';
import messages from './MiniProduct.messages';

/**
 * Render component for a single Product card.
 *
 * @visibleName Mini Product Grid: Mini Product
 * @author [Nathan Moore](https://github.com/nathandmoore)
 */
const MiniProduct = ({ product, position, pricing }) => {
  const {
    containerName,
    query,
    usingGrid = true
  } = React.useContext(MiniProductGridContext);

  const matchType = React.useMemo(() => {
    if (isEmpty(query)) {
      return null;
    }

    const lowerQuery = toLower(query);
    const {
      sku,
      interchangePartNumbers = [],
      alternateForParts = [],
      dealerPartNumber
    } = product;

    if (toLower(sku) === lowerQuery) {
      return messages.partNumberMatch;
    }

    if (toLower(dealerPartNumber) === lowerQuery) {
      return messages.dealerPartNumberMatch;
    }

    if (interchangePartNumbers.some(n => toLower(n) === lowerQuery)) {
      return messages.xRefMatch;
    }

    if (alternateForParts.some(n => toLower(n) === lowerQuery)) {
      return messages.altPartMatch;
    }

    if (toLower(sku).includes(lowerQuery)) {
      return messages.partialPartNumberMatch;
    }

    if (dealerPartNumber && toLower(dealerPartNumber).includes(lowerQuery)) {
      return messages.dealerPartialPartNumberMatch;
    }

    if (interchangePartNumbers.some(n => toLower(n).includes(lowerQuery))) {
      return messages.partialXRefMatch;
    }

    if (alternateForParts.some(n => toLower(n).includes(lowerQuery))) {
      return messages.partialAltPartMatch;
    }

    return null;
  }, [product, query]);

  const gtmItem = React.useMemo(
    () => ({
      brand: get(product, 'attributes.BRAND.value'),
      category: get(product, 'parentCategories[0].name'),
      id: product.sku,
      name: product.description,
      price: get(product, 'priceInfo.price.amount', 0.0),
      position,
      quantity: 1,
      containerName
    }),
    [containerName, product, position]
  );

  const [isInventoryModalOpen, setInventoryModalOpen] = React.useState(false);

  return (
    <li
      className={classNames(
        'MiniProduct group flex-grow-0 mb-4 pb-4 border-b border-solid border-gray-300 last:border-b-0 sm:flex sm:flex-wrap sm:justify-end sm:p-3 overflow-hidden',
        {
          'basis-0 sm:flex-col sm:flex-shrink-0 sm:basis-1/2 sm:border-b-0 md:basis-1/3':
            usingGrid,
          'sm:flex-row sm:flex-no-wrap sm:items-start sm:border-b sm:border-solid sm:last:border-b-0':
            !usingGrid
        }
      )}
    >
      <MiniProduct.Asset
        className={classNames('relative float-left w-2/5 pr-4', {
          'sm:w-full': usingGrid,
          'sm:w-1/5': !usingGrid
        })}
        gtmItem={gtmItem}
        product={product}
      />
      <MiniProduct.Body
        className={classNames('flex flex-col flex-grow w-3/5', {
          'flex-grow flex-shrink-0 basis-0 sm:w-full sm:mt-4': usingGrid,
          'sm:w-2/5 sm:pr-2': !usingGrid
        })}
        gtmItem={gtmItem}
        product={product}
      />
      <MiniProduct.Footer
        className={classNames('flex flex-col w-3/5 ml-auto', {
          'sm:w-full': usingGrid,
          'sm:w-1/3': !usingGrid
        })}
        product={product}
        gtmItem={gtmItem}
        pricing={pricing}
        isInventoryModalOpen={isInventoryModalOpen}
        setInventoryModalOpen={setInventoryModalOpen}
        matchType={matchType}
      />
    </li>
  );
};

const Asset = ({ className, gtmItem, product }) => {
  const url = useProductUrl(product.uri);
  const primaryAsset = get(product, 'primaryAsset', {
    altText: product.primaryAssetAltText,
    contentUrl: product.primaryAssetContentUrl,
    tags: product.primaryAssetTags,
    title: product.primaryAssetTitle,
    type: product.primaryAssetType
  });
  return (
    <div className={className}>
      <MiniProductAsset
        availableOnline={product.availableOnline}
        productName={product.name}
        primaryAsset={primaryAsset}
        productUrl={url}
        gtmItem={gtmItem}
      />
    </div>
  );
};

const Body = ({ className, gtmItem, product }) => {
  const formatMessage = useFormatMessage();
  const { promotionMessage } = product;
  const url = useProductUrl(product.uri);
  const sendProductClick = useProductClick(gtmItem);
  const { breadcrumbs = [] } = React.useContext(MiniProductGridContext);

  // -- this block is to fix a safari bug when using basis-0 or basis-% for children of flex-col
  const [minHeight, setMinHeight] = React.useState('0');
  const nameRef = React.useRef(null);
  const descriptionRef = React.useRef(null);
  const promoRef = React.useRef(null);
  const dpnRef = React.useRef(null);
  const updateMinHeight = React.useCallback(
    () =>
      throttle(() => {
        const newHeight =
          get(nameRef, 'current.offsetHeight', 0) +
          get(descriptionRef, 'current.offsetHeight', 0) +
          get(promoRef, 'current.offsetHeight', 0) +
          get(dpnRef, 'current.offsetHeight', 0);

        setMinHeight(newHeight);
      }, 100),
    []
  );

  useWindowEventListener('resize', updateMinHeight);

  React.useEffect(() => {
    const newHeight =
      get(nameRef, 'current.offsetHeight', 0) +
      get(descriptionRef, 'current.offsetHeight', 0) +
      get(promoRef, 'current.offsetHeight', 0) +
      get(dpnRef, 'current.offsetHeight', 0);

    setMinHeight(newHeight);
  }, []);
  // -- this block is to fix a safari bug when using basis-0 or basis-% for children of flex-col

  return (
    <header className={className} style={{ minHeight: `${minHeight + 32}px` }}>
      <PartNumber
        ref={nameRef}
        url={url}
        breadcrumbs={breadcrumbs}
        product={product}
        sendProductClick={sendProductClick}
      />
      <DealerPartNumber product={product} ref={dpnRef} />
      <ProductDescription
        ref={descriptionRef}
        url={url}
        breadcrumbs={breadcrumbs}
        product={product}
        sendProductClick={sendProductClick}
      />
      <PromotionMessage promotion={promotionMessage} ref={promoRef} />
    </header>
  );
};

const PartNumber = React.forwardRef(
  ({ url, breadcrumbs, product, sendProductClick }, ref) => {
    return (
      <h4
        ref={ref}
        className="font-bold leading-snug text-base text-gray-700 uppercase"
      >
        <Button
          className="text-gray-700 appearance-none group-hover:text-gray-700 focus:text-gray-700 focus:outline-none"
          to={location => ({
            ...location,
            pathname: url,
            state: {
              ...get(location, 'state', {}),
              breadcrumbs: breadcrumbs.concat([{ label: product.name }])
            }
          })}
          onClick={() => {
            sendProductClick();
          }}
        >
          {product.name}
        </Button>
      </h4>
    );
  }
);

PartNumber.displayName = 'PartNumber';

const MatchType = React.forwardRef(({ matchType }, ref) => {
  const formatMessage = useFormatMessage();

  if (!matchType) {
    return null;
  }

  return (
    <div
      ref={ref}
      className="inline-block self-start px-2 py-1 mt-1 text-xs font-semibold leading-tight"
      style={{
        backgroundColor: '#D1D1D1',
        color: '#404040'
      }}
    >
      {formatMessage(matchType)}
    </div>
  );
});

MatchType.displayName = 'MatchType';

const DealerPartNumber = React.forwardRef(({ product }, ref) => {
  const formatMessage = useFormatMessage();
  const { isWhiteLabel } = useNationalSiteContext();
  const dealerPartNumber = product.dealerPartNumber;

  if (isEmpty(dealerPartNumber) || !isWhiteLabel) {
    return null;
  }

  return (
    <h3 ref={ref}>
      <span className="text-gray-600">
        {formatMessage(messages.dealerPartNumber)}
      </span>
      <span className="ml-1 text-gray-900">{dealerPartNumber}</span>
    </h3>
  );
});

DealerPartNumber.displayName = 'DealerPartNumber';

const ProductDescription = React.forwardRef(
  ({ url, breadcrumbs, product, sendProductClick }, ref) => {
    return (
      <h5
        ref={ref}
        className="mt-2 mb-2 leading-snug text-sm uppercase md:text-base"
      >
        <Button
          className="text-gray-600 appearance-none break-normal focus:text-gray-700 focus:outline-none"
          to={location => ({
            ...location,
            pathname: url,
            state: {
              ...get(location, 'state', {}),
              breadcrumbs: breadcrumbs.concat([{ label: product.name }])
            }
          })}
          onClick={() => {
            sendProductClick();
          }}
        >
          {toLower(product.description)}
        </Button>
      </h5>
    );
  }
);

ProductDescription.displayName = 'ProductDescription';

const PromotionMessage = React.forwardRef(({ promotion }, ref) => {
  if (isEmpty(promotion)) {
    return null;
  }
  return <p ref={ref}>{promotion}</p>;
});

PromotionMessage.displayName = 'PromotionMessage';

const Footer = ({
  className,
  product,
  gtmItem,
  pricing,
  isInventoryModalOpen,
  setInventoryModalOpen,
  matchType
}) => {
  const sendProductClick = useProductClick(gtmItem);
  const { breadcrumbs = [] } = React.useContext(MiniProductGridContext);
  const [quantity, setQuantity] = React.useState(1);
  const inventoryTargetRef = React.useRef(null);
  const [, forceUpdate] = React.useState();

  React.useEffect(() => {
    if (inventoryTargetRef.current) {
      forceUpdate({});
    }
  }, []);

  return (
    <footer className={className}>
      <MatchType matchType={matchType} />
      <InventoryIndicatorModal
        isOpen={isInventoryModalOpen}
        onClose={() => {
          setInventoryModalOpen(false);
        }}
        locationQtys={get(product, 'inventorySummary.locationQuantities', {})}
        partNumber={product?.sku || ''}
        quantity={quantity}
        targetDOMNode={inventoryTargetRef.current}
        mini={true}
      />
      <MiniProductPrices pricing={pricing} />
      <CoreCost
        className="text-gray-700"
        coreCost={get(pricing, 'coreCost', get(product, 'coreCost'))}
        message={messages.coreCost}
      />
      <ItemAvailability
        product={product}
        textSize={'text-sm'}
        nearbyButtonSize={TertiaryButton.Size.SMALL}
        containerRef={inventoryTargetRef}
      />
      <MiniProductAddToCart
        product={product}
        sendProductClick={sendProductClick}
        breadcrumbs={breadcrumbs}
        setInventoryModalOpen={setInventoryModalOpen}
        quantity={quantity}
        setQuantity={setQuantity}
      />
    </footer>
  );
};

MiniProduct.Asset = Asset;
MiniProduct.Body = Body;
MiniProduct.Footer = Footer;

MiniProduct.propTypes = {
  product: PropTypes.shape({
    priceInfo: PropTypes.shape({
      price: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired
      }).isRequired,
      priceType: PropTypes.oneOf(values(PriceType)).isRequired,
      priceTypeDetails: PropTypes.shape(
        values(PriceType).reduce((acc, type) => {
          acc[type] = PropTypes.shape({
            bestPrice: PropTypes.shape({
              amount: PropTypes.number.isRequired,
              currency: PropTypes.string.isRequired
            }).isRequired,
            type: PropTypes.string.isRequired
          });
          return acc;
        }, {})
      )
    }),
    /** The ID of the product */
    id: PropTypes.string.isRequired,
    /** The display name of the product */
    name: PropTypes.string.isRequired,
    primaryAsset: PropTypes.shape({
      altText: PropTypes.string,
      contentUrl: PropTypes.string,
      /** HTML to embed in place of having a source URL */
      embedCode: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
      title: PropTypes.string,
      type: PropTypes.oneOf(values(AssetType))
    }),
    /* the search service will return these 4 fields instead of a `primaryAsset` */
    primaryAssetAltText: PropTypes.string,
    primaryAssetContentUrl: PropTypes.string,
    primaryAssetTags: PropTypes.arrayOf(PropTypes.string),
    primaryAssetTitle: PropTypes.string,
    primaryAssetType: PropTypes.oneOf(values(AssetType)),
    /** The inventory details of the product*/
    availableOnline: PropTypes.bool,
    /** The URI of the details page related to this product */
    uri: PropTypes.string
  }),
  /** Whether grid view is in use or if it's list */
  usingGrid: PropTypes.bool
};

export default MiniProduct;
export { MiniProduct };
